import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { config } from '../config';
import { 
  Phone, 
  Lock, 
  Eye,
  EyeOff,
  LogIn,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const apiUrl = config.apiUrl;

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        phone: '',
        password: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.phone.trim()) {
            newErrors.phone = 'ফোন নম্বর প্রয়োজন';
        } else if (!/^[0-9+]{11,15}$/.test(formData.phone)) {
            newErrors.phone = 'সঠিক ফোন নম্বর দিন';
        }

        if (!formData.password) {
            newErrors.password = 'পাসওয়ার্ড প্রয়োজন';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setErrors({});

        try {
            const response = await axios.post(`${apiUrl}/login`, formData);

            console.log(response.data)

            if (response.data.success) {
                const { token, user } = response.data;

                console.log(response.data)

                // Store data in localStorage
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));
                
                if(user.approved===1){
                    if(user.role==="admin"){
                        navigate('/dashboard');
                    }else{
                        navigate('/');
                    }
                }else{
                    alert("Your Account is not Approved!")
                }
               
               
            } else {
                setErrors({ submit: response.data.message });
            }
        } catch (error) {
            console.error('Login error:', error);
            
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else if (error.response?.data?.message) {
                setErrors({ submit: error.response.data.message });
            } else {
                setErrors({ submit: 'লগইন ব্যর্থ হয়েছে। অনুগ্রহ করে আপনার credentials চেক করুন।' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-center">
                    <h1 className="text-2xl font-bold text-white">লগইন করুন</h1>
                    <p className="text-blue-100 mt-2">আপনার প্রশ্ন ব্যাংক একাউন্টেアクセス করুন</p>
                </div>

                <div className="p-6">
                    {/* Error Message */}
                    {errors.submit && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            <span className="text-red-800 text-sm">{errors.submit}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Phone Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                                ফোন নম্বর *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="01XXXXXXXXX"
                                required
                            />
                            {errors.phone && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {errors.phone}
                                </p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Lock className="h-4 w-4 mr-2 text-blue-600" />
                                পাসওয়ার্ড *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                        errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="আপনার পাসওয়ার্ড লিখুন"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        {/* Forgot Password Link */}
                        <div className="text-right">
                            <Link
                                to="/forgot-password"
                                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                পাসওয়ার্ড ভুলে গেছেন?
                            </Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    লগইন হচ্ছে...
                                </>
                            ) : (
                                <>
                                    <LogIn className="h-5 w-5 mr-2" />
                                    লগইন করুন
                                </>
                            )}
                        </button>
                    </form>

                    {/* Register Link */}
                    <div className="mt-6 text-center">
                        <p className="text-gray-600">
                            একাউন্ট নেই?{' '}
                            <Link
                                to="/register"
                                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                            >
                                রেজিস্টার করুন
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Demo Credentials (optional) */}
                <div className="bg-gray-50 p-4 border-t">
                    <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">ডেমো একাউন্ট:</p>
                        <div className="text-xs text-gray-500 space-y-1">
                            <p>ফোন: 01234567890</p>
                            <p>পাসওয়ার্ড: password123</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;